import { useTranslation } from "react-i18next"

import type { Artifact, ArtifactType } from "@/api/models"
import type { LucideIcon } from "@/components/icons"

import {
  Check,
  CheckSquare,
  ChevronDown,
  Clock,
  FileSpreadsheet,
  FileText,
  FileType,
  HelpCircle,
  Image as ImageIcon,
  ListTodo,
  Mail,
  Presentation,
  Sparkles,
  Table as TableIcon,
} from "@/components/icons"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

import { type ArtifactLineage, getArtifactTitle } from "./artifact-selector-utils"

const TYPE_ICON: Record<ArtifactType, LucideIcon> = {
  document: FileText,
  short_answer: Sparkles,
  email_draft: Mail,
  table: TableIcon,
  list: ListTodo,
  docx: FileText,
  xlsx: FileSpreadsheet,
  pptx: Presentation,
  pdf: FileType,
  image: ImageIcon,
  diagram: FileType,
  action_receipt: CheckSquare,
  pending_action: Clock,
  ask_user: HelpCircle,
  plan: ListTodo,
  unknown: HelpCircle,
}

export interface ArtifactSelectorProps {
  activeArtifact: Artifact
  isLatestUnseen: boolean
  lineages: ArtifactLineage[]
  onSelect: (artifactId: string) => void
}

export function ArtifactSelector({
  activeArtifact,
  isLatestUnseen,
  lineages,
  onSelect,
}: ArtifactSelectorProps) {
  const { t } = useTranslation()
  const lookup = TYPE_ICON as Record<string, LucideIcon | undefined>
  const Icon = lookup[activeArtifact.type] ?? HelpCircle
  const title = getArtifactTitle(activeArtifact)
  // Pick a category label by the active artifact's type so the trigger
  // doesn't repeat the per-artifact filename (e.g. the GDPR deliverable's
  // title was rendered both in the trigger and in the dropdown). The plan
  // gets its own label because calling it a "deliverable" is misleading
  // when the user is viewing the research plan itself.
  let categoryLabel: string
  if (activeArtifact.type === "plan") {
    categoryLabel = t("deepExplorer.research_plan_label", { defaultValue: "Research plan" })
  } else if (activeArtifact.type === "ask_user") {
    categoryLabel = t("deepExplorer.quick_question_label", { defaultValue: "Quick question" })
  } else {
    categoryLabel = t("deepExplorer.deliverables_label", { defaultValue: "Deliverables" })
  }
  const triggerLabel = isLatestUnseen
    ? t("deepExplorer.latest_artifact_available", {
        defaultValue: "Latest artifact available",
      })
    : t("deepExplorer.artifact_selector", {
        defaultValue: "Select workspace artifact",
      })
  const triggerClassName = cn(
    "border-primary bg-primary/10 text-foreground relative inline-flex max-w-[22rem] items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs",
    "hover:bg-accent hover:text-accent-foreground transition-colors"
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={triggerLabel}
          className={triggerClassName}
          data-artifact-id={activeArtifact.id}
          data-testid="workspace-artifact-selector-trigger"
          type="button"
        >
          <Icon aria-hidden className="size-3.5 shrink-0" />
          <span className="truncate" title={title}>
            {categoryLabel}
          </span>
          <span className="text-muted-foreground shrink-0">v{activeArtifact.version}</span>
          <ChevronDown aria-hidden className="size-3.5 shrink-0" />
          {isLatestUnseen && (
            <span
              aria-hidden
              className="bg-primary ring-background absolute -top-1 -right-1 size-2 animate-pulse rounded-full ring-2 motion-reduce:animate-none"
              data-testid="latest-unseen-pulse"
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        {lineages.map((lineage, lineageIndex) => {
          const isPlanLineage = lineage.latest.type === "plan"
          const isAskUserLineage = lineage.latest.type === "ask_user"
          // Group header mirrors the pill's category label so the dropdown
          // and trigger stay consistent — the per-artifact title belongs
          // on the individual item rows below (Erik feedback 2026-05-11).
          let groupLabel: string
          if (isPlanLineage) {
            groupLabel = t("deepExplorer.research_plan_label", { defaultValue: "Research plan" })
          } else if (isAskUserLineage) {
            groupLabel = t("deepExplorer.quick_question_label", { defaultValue: "Quick question" })
          } else {
            groupLabel = t("deepExplorer.deliverables_label", { defaultValue: "Deliverables" })
          }
          const LineageIcon = lookup[lineage.latest.type] ?? HelpCircle
          return (
            <div key={lineage.latest.id}>
              {lineageIndex > 0 ? <DropdownMenuSeparator /> : null}
              <DropdownMenuLabel
                className="text-muted-foreground flex items-center gap-2 text-xs font-medium"
                data-testid="workspace-artifact-group-label"
              >
                <LineageIcon aria-hidden className="size-3.5 shrink-0" />
                <span className="truncate" title={groupLabel}>
                  {groupLabel}
                </span>
              </DropdownMenuLabel>
              {[...lineage.versions].reverse().map((version) => {
                const isActive = version.id === activeArtifact.id
                // Plan rows are localized; deliverable rows use the artifact's own title
                // (that's the whole point — the title lives on the deliverable object).
                const versionTitle = isPlanLineage ? groupLabel : getArtifactTitle(version)
                return (
                  <DropdownMenuItem
                    key={version.id}
                    className="gap-2"
                    data-testid={`workspace-artifact-option-${version.id}`}
                    onSelect={() => onSelect(version.id)}
                  >
                    <span className="text-muted-foreground w-7 shrink-0 text-xs tabular-nums">
                      v{version.version}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{versionTitle}</span>
                    {version.id === lineage.latest.id ? (
                      <span className="text-muted-foreground text-[10px]">
                        {t("deepExplorer.current_version", { defaultValue: "current" })}
                      </span>
                    ) : null}
                    {isActive ? <Check aria-hidden className="text-primary size-4" /> : null}
                  </DropdownMenuItem>
                )
              })}
            </div>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
